// useDetailPanel — U42 Detail Panel URL 동기화 훅.
//
// ?item={id} 쿼리 파라미터를 SoT로 사용한다.
// open(id)  → searchParams.set('item', String(id))
// close()   → searchParams.delete('item')
// itemId    → 현재 열린 항목 ID (null이면 닫힌 상태)
//
// panelWidth / setPanelWidth: 사용자 resize 값. localStorage에 영속.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useItem } from './useItems';

// localStorage 키 상수
const WIDTH_STORAGE_KEY = 'locker:detail-panel-width';

// 폭 범위 제약
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 420;

/**
 * localStorage에서 저장된 패널 폭을 읽는다.
 * 없거나 범위를 벗어나면 DEFAULT_WIDTH를 반환.
 */
function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (raw === null) return DEFAULT_WIDTH;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < MIN_WIDTH || n > MAX_WIDTH) return DEFAULT_WIDTH;
    return n;
  } catch {
    return DEFAULT_WIDTH;
  }
}

export interface UseDetailPanelReturn {
  /** 현재 열린 항목 ID. null이면 패널 닫힘. */
  itemId: number | null;
  /** 패널 열기 — URL ?item={id} 설정 */
  open: (id: number) => void;
  /** 패널 닫기 — URL ?item 제거 */
  close: () => void;
  /** 이전 항목으로 이동. prevIds가 없으면 no-op. */
  goPrev: () => void;
  /** 다음 항목으로 이동. nextIds가 없으면 no-op. */
  goNext: () => void;
  /** 이전/다음 이동용 항목 ID 목록 설정 */
  setNavigationIds: (ids: number[]) => void;
  /** 패널 폭(px). 320~600 사이. */
  panelWidth: number;
  /** 패널 폭 변경 (resize handle이 호출) */
  setPanelWidth: (w: number) => void;
}

/**
 * Detail Panel 상태 관리 훅.
 * URL ?item 파라미터를 SoT로 사용하므로 브라우저 히스토리와 동기화된다.
 */
export function useDetailPanel(): UseDetailPanelReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL에서 itemId 파싱
  const itemParam = searchParams.get('item');
  const itemId = itemParam !== null && !isNaN(Number(itemParam)) ? Number(itemParam) : null;

  // 이전/다음 이동용 목록
  const [navigationIds, setNavigationIds] = useState<number[]>([]);

  // 패널 폭 — localStorage 초기화
  const [panelWidth, setPanelWidthState] = useState<number>(() => readStoredWidth());

  // 폭 변경 시 localStorage 저장
  const setPanelWidth = useCallback((w: number) => {
    const clamped = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));
    setPanelWidthState(clamped);
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
    } catch {
      // localStorage 쓰기 실패 시 무시 (보안 컨텍스트 제한 등)
    }
  }, []);

  const open = useCallback(
    (id: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('item', String(id));
          // 항목을 열면 등록 모드(?new)는 종료 — 등록 저장 직후 onClose(new 제거)와 open이
          // 같은 틱에 호출되면 뒤 호출이 앞 결과를 덮어써 등록 폼이 남던 문제 (2026-10-03)
          next.delete('new');
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('item');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // 이전 항목으로 이동
  const goPrev = useCallback(() => {
    if (itemId === null) return;
    const idx = navigationIds.indexOf(itemId);
    if (idx > 0) {
      open(navigationIds[idx - 1]);
    }
  }, [itemId, navigationIds, open]);

  // 다음 항목으로 이동
  const goNext = useCallback(() => {
    if (itemId === null) return;
    const idx = navigationIds.indexOf(itemId);
    if (idx !== -1 && idx < navigationIds.length - 1) {
      open(navigationIds[idx + 1]);
    }
  }, [itemId, navigationIds, open]);

  return {
    itemId,
    open,
    close,
    goPrev,
    goNext,
    setNavigationIds,
    panelWidth,
    setPanelWidth,
  };
}

/**
 * 현재 패널에 열린 항목 데이터 조회 훅.
 * useDetailPanel과 분리하여 데이터 로딩 책임을 명확히 한다.
 */
export function useDetailPanelItem(itemId: number | null) {
  return useItem(itemId);
}

// 폭 관련 상수 재사용 허용
export { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH, WIDTH_STORAGE_KEY };
